import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useDailyQuest, useQuests, useTags, type QuestSort } from "../api/queries";
import { QuestCard } from "../components/QuestCard";
import { tagIndex, tagName } from "../lib/tags";
import { EmptyState, ErrorState, Skeleton } from "../components/ui";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { usePageMeta } from "../lib/usePageMeta";

const PAGE_SIZE = 12;

/** Сколько тегов показывать без поиска. Остальные достаются поиском. */
const VISIBLE_TAGS = 10;

/** Сколько показывать найденных: длинный список ищут глазами дольше, чем набирают. */
const MAX_FOUND_TAGS = 24;

/**
 * Перемешать, не трогая исходный массив.
 *
 * Тасование Фишера — Йетса, а не sort со случайным компаратором: тот даёт
 * неравномерный результат и в некоторых движках вообще ломается на
 * непоследовательном компараторе.
 */
function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function QuestListPage() {
  const { t } = useTranslation();
  usePageMeta({ title: t("nav.quests"), description: t("seo.quests") });
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Направление живёт рядом с полем: повторное нажатие на ту же кнопку
  // переворачивает список, а не выбирает другой порядок.
  const [sort, setSort] = useState<QuestSort>("QUEST_SORT_CREATED");
  const [ascending, setAscending] = useState(false);
  const [tagQuery, setTagQuery] = useState("");

  const chooseSort = (next: QuestSort) => {
    setAscending((current) => (next === sort ? !current : false));
    setSort(next);
    setPage(1);
  };

  // Запрос уходит не на каждую букву: иначе набор фразы даёт десяток запросов.
  const debouncedSearch = useDebouncedValue(search, 350);

  const daily = useDailyQuest();
  const quests = useQuests({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
    tags: activeTag ? [activeTag] : undefined,
    sort,
    ascending,
  });

  const total = Number(quests.data?.page?.total ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Теги берутся из справочника целиком, а не из того, что попало на
  // страницу: иначе фильтр показывает только теги видимых квестов и не даёт
  // добраться до остальных.
  //
  // Их может быть сколько угодно — сотня, тысяча, — поэтому на виду только
  // первые, а до любого другого доводит поиск рядом.
  // Ссылка на массив из кэша стабильна, а `?? []` создавал бы новый пустой
  // на каждом рендере — и перемешивание пересчитывалось бы под курсором.
  const loadedTags = useTags().data?.tags;
  const allTags = useMemo(() => loadedTags ?? [], [loadedTags]);
  const index = tagIndex(allTags);

  const query = tagQuery.trim().toLowerCase();
  const matching = allTags.filter((tag) => {
    if (!query) return true;
    const slug = tag.slug ?? "";
    return slug.toLowerCase().includes(query) || tagName(tag, slug).toLowerCase().includes(query);
  });

  // Без поиска на виду случайная десятка, а не первая по алфавиту: иначе
  // каталог всегда предлагает «1890-е, 1900-е, 1910-е», и половина
  // справочника не показывается никому никогда.
  //
  // Перемешивание запоминается на время жизни страницы: пересчитывать его
  // на каждый рендер значит менять теги местами под курсором.
  const shuffled = useMemo(() => shuffle(allTags), [allTags]);

  // Выбранный тег виден всегда, даже если он не попал ни в первые, ни в
  // найденные: иначе фильтр действует, а чем именно — не видно.
  const source = query ? matching : shuffled;
  const shown = source.slice(0, query ? MAX_FOUND_TAGS : VISIBLE_TAGS).map((tag) => tag.slug ?? "");
  const tags = activeTag && !shown.includes(activeTag) ? [activeTag, ...shown] : shown;
  const hidden = source.length - shown.length;

  return (
    <div className="space-y-6 sm:space-y-8">
      {daily.data && (
        <Link
          to={`/quests/${daily.data.id}`}
          className="panel group flex flex-col gap-3 overflow-hidden p-4 transition-colors
                     hover:border-amber-400/40 sm:flex-row sm:items-center sm:gap-4 sm:p-5"
        >
          {daily.data.coverImageUrl && (
            <img
              src={daily.data.coverImageUrl}
              alt=""
              className="h-24 w-full rounded-xl object-cover sm:h-32 sm:w-52"
            />
          )}
          <div className="flex-1">
            <span className="chip border-amber-400/40 text-amber-300">{t("quest.daily")}</span>
            <h2 className="mt-2 font-sans text-xl text-ink-50 sm:text-2xl">{daily.data.title}</h2>
            {/* Пояснение про общий набор кадров — для тех, кто видит квест
                дня впервые. На телефоне строка стоит целого абзаца высоты
                над кнопкой, ради которой блок и существует. */}
            <p className="mt-1 hidden text-sm text-ink-400 sm:block">
              {t("quests.dailyLead")}
            </p>
          </div>
          <span className="btn-primary w-full shrink-0 justify-center text-center sm:w-auto sm:self-center">{t("landing.play")}</span>
        </Link>
      )}

      <div>
        <h1 className="font-sans text-2xl text-ink-50 sm:text-3xl">{t("quests.title")}</h1>
        <p className="mt-1 text-sm text-ink-400">
          {total > 0 ? t("quests.found", { count: total }) : t("quests.subtitle")}
        </p>
      </div>

      <div className="space-y-3">
        <input
          className="field"
          placeholder={t("quests.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />

        {/* Сортировка. Стрелка показывает направление у выбранной кнопки —
            по одной подписи «по дате» нельзя понять, свежие сверху или
            старые. */}
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["QUEST_SORT_CREATED", "quests.sortCreated"],
              ["QUEST_SORT_VIEWS", "quests.sortViews"],
              ["QUEST_SORT_RATING", "quests.sortRating"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => chooseSort(value)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs
                          font-medium transition-colors ${
                            sort === value
                              ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
                              : "border-ink-700 text-ink-400 hover:border-ink-600 hover:text-ink-200"
                          }`}
            >
              {t(label)}
              {sort === value && <span aria-hidden>{ascending ? "↑" : "↓"}</span>}
            </button>
          ))}
        </div>

        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Поиск стоит первым в ряду тегов, а не отдельной строкой:
                это одно и то же действие — выбрать тег. */}
            <input
              className="field w-40 py-1.5 text-xs"
              placeholder={t("quests.tagSearch")}
              value={tagQuery}
              onChange={(event) => setTagQuery(event.target.value)}
            />

            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  setActiveTag((current) => (current === tag ? null : tag));
                  setPage(1);
                }}
                className={`chip transition-colors ${
                  activeTag === tag
                    ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
                    : "hover:border-ink-500 hover:text-ink-100"
                }`}
              >
                {tagName(index.get(tag), tag)}
              </button>
            ))}

            {hidden > 0 && (
              <span className="text-xs text-ink-500">{t("quests.tagsHidden", { count: hidden })}</span>
            )}
            {query && matching.length === 0 && (
              <span className="text-xs text-ink-500">{t("quests.tagsNotFound")}</span>
            )}
          </div>
        )}
      </div>

      {quests.isError && (
        <ErrorState message={t("quests.loadFailed")} onRetry={() => void quests.refetch()} />
      )}

      {quests.isPending && (
        <div className="grid gap-3 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-80" />
          ))}
        </div>
      )}

      {quests.data && quests.data.quests?.length === 0 && (
        <EmptyState
          title={t("quests.emptyTitle")}
          description={t("quests.emptyText")}
          action={
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setSearch("");
                setActiveTag(null);
                setPage(1);
              }}
            >
              {t("quests.resetFilters")}
            </button>
          }
        />
      )}

      {quests.data && (quests.data.quests?.length ?? 0) > 0 && (
        <div className="grid gap-3 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {quests.data.quests?.map((quest) => (
            <QuestCard key={quest.id} quest={quest} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            {t("common.back")}
          </button>
          <span className="tabular-nums text-sm text-ink-400">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            {t("common.forward")}
          </button>
        </div>
      )}
    </div>
  );
}
